import React from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useContent } from "../hooks/useContent";

const Diagram = () => (
    <svg viewBox="0 0 600 600" className="w-full h-full">
        <defs>
            <linearGradient id="diagBg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fbfbfd" />
                <stop offset="100%" stopColor="#ececef" />
            </linearGradient>
        </defs>
        <rect width="600" height="600" fill="url(#diagBg)" />
        <g
            stroke="#0a84ff"
            strokeWidth="0.7"
            fill="none"
            opacity="0.45"
            strokeDasharray="3 4"
        >
            {[80, 160, 240, 320, 400, 480].map((y) => (
                <line key={y} x1="40" y1={y} x2="560" y2={y} />
            ))}
            {[80, 160, 240, 320, 400, 480].map((x) => (
                <line key={x} x1={x} y1="40" x2={x} y2="560" />
            ))}
        </g>

        {/* Big handpiece illustration */}
        <g transform="translate(60,180)">
            <defs>
                <linearGradient id="bodyG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="100%" stopColor="#c7cad0" />
                </linearGradient>
            </defs>
            <rect x="0" y="40" width="360" height="60" rx="14" fill="url(#bodyG)" stroke="#8e8e93" strokeWidth="0.6" />
            <rect x="200" y="40" width="10" height="60" fill="#00e5ff" />
            <path d="M360 60 L398 30 L420 38 L398 70 L420 86 L398 100 L420 116 L398 130 L360 100 Z" fill="url(#bodyG)" stroke="#8e8e93" strokeWidth="0.6" />
            <circle cx="418" cy="80" r="44" fill="url(#bodyG)" stroke="#8e8e93" strokeWidth="0.6" />
            <circle cx="418" cy="80" r="30" fill="#1c1c1e" />
            <circle cx="418" cy="80" r="6" fill="#00e5ff" />
        </g>

        {/* Callouts */}
        <g stroke="#0a84ff" strokeWidth="0.8" fill="none">
            <line x1="240" y1="220" x2="240" y2="120" />
            <line x1="240" y1="120" x2="180" y2="120" />
            <circle cx="240" cy="220" r="3" fill="#0a84ff" />
        </g>
        <text x="80" y="115" fontSize="11" fontFamily="JetBrains Mono" fill="#0a84ff">
            ROTOR CÉRAMIQUE
        </text>

        <g stroke="#0a84ff" strokeWidth="0.8" fill="none">
            <line x1="478" y1="260" x2="500" y2="380" />
            <line x1="500" y1="380" x2="560" y2="380" />
            <circle cx="478" cy="260" r="3" fill="#0a84ff" />
        </g>
        <text x="430" y="396" fontSize="11" fontFamily="JetBrains Mono" fill="#0a84ff">
            TÊTE 45° · 22 W
        </text>

        <g stroke="#0a84ff" strokeWidth="0.8" fill="none">
            <line x1="120" y1="280" x2="100" y2="420" />
            <line x1="100" y1="420" x2="200" y2="420" />
            <circle cx="120" cy="280" r="3" fill="#0a84ff" />
        </g>
        <text x="100" y="438" fontSize="11" fontFamily="JetBrains Mono" fill="#0a84ff">
            CORPS TITANE 316L
        </text>

        {/* Dimension */}
        <line x1="60" y1="510" x2="540" y2="510" stroke="#8e8e93" strokeDasharray="3 3" />
        <text x="280" y="500" fontSize="11" fontFamily="JetBrains Mono" fill="#8e8e93" textAnchor="middle">
            142.0 mm
        </text>
    </svg>
);

const EngineeringExcellence = () => {
    const { data, isLoading } = useContent("engineering");
    const content = data?.data || {};
    const features = content.features || [];

    return (
        <section id="expertise" className="relative bg-white py-24 md:py-32 border-y border-black/[0.06]">
            <div className="max-w-[1320px] mx-auto px-6 md:px-10">
                <div className="max-w-3xl">
                    <div className="dp-eyebrow">{content.eyebrow || "Ingénierie"}</div>
                    <h2 className="mt-3 font-display text-4xl md:text-5xl lg:text-[56px] font-light tracking-tight text-neutral-900 leading-[1.05]">
                        {content.title ? (
                            content.title
                        ) : (
                            <>
                                L'ingénierie au service{" "}
                                <span className="italic text-neutral-500">du geste clinique.</span>
                            </>
                        )}
                    </h2>
                    <p className="mt-5 text-[17px] text-neutral-500 max-w-2xl">
                        {content.subtitle ||
                            "Chaque instrument est conçu, testé et calibré pour résister aux exigences du cabinet. Voici ce qui se cache à l'intérieur."}
                    </p>
                </div>

                {isLoading && (
                    <div className="mt-16 flex justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-[#0a84ff]" />
                    </div>
                )}

                <div className="mt-16 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    {/* Sticky diagram */}
                    <div className="lg:sticky lg:top-[100px]">
                        <div className="relative aspect-square rounded-3xl overflow-hidden bg-[#FBFBFD] border border-black/[0.06]">
                            <Diagram />
                        </div>
                        <div className="mt-4 flex items-center gap-2 text-[12px] font-mono-tech tracking-[0.2em] uppercase text-neutral-500">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#0a84ff]" />
                            Schéma technique · TURBINE 45°
                        </div>
                    </div>

                    {/* Feature list */}
                    <div className="space-y-12">
                        {features.map((f, i) => (
                            <motion.div
                                key={f.title}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.55, delay: i * 0.04 }}
                                className="relative pl-7 border-l border-neutral-200"
                            >
                                <div className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-[#1C1C1E] ring-4 ring-white" />
                                <div className="dp-eyebrow">{f.kicker}</div>
                                <h3 className="mt-2 font-display text-2xl md:text-3xl tracking-tight font-medium text-neutral-900">
                                    {f.title}
                                </h3>
                                <p className="mt-3 text-[16px] leading-relaxed text-neutral-500 max-w-lg">
                                    {f.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EngineeringExcellence;
